import type {
  AllStageSupplierUserListItem,
  ApiResponse,
  AuditSupplierFileSchema,
  SavePerformanceEvaluationSchema,
  PageResponse,
  PerformanceEvaluationListItem,
  PerformanceEvaluationListParams,
  PurchasePerformanceEvaluationIndicator,
  QuerySupplierUserAllStageSchema,
  SaveOrUpdateSupplierPerformanceEvaluationIndicatorSchema,
  SaveSupplementaryFileSchema,
  SupplierRelationListItem,
  SupplierUserAccountListItem,
  SupplierUserSimpleListItem,
  SupplierUserSimpleListParams,
  UpdateSupplierMaterialSchema,
  PerformanceEvaluationDetail,
  UpdatePerformanceEvaluationAdviceSchema,
  QueryNoticePageList,
  NoticeInfoListItem,
  SupplierBlockListItem,
  SupplierBlacklistItem,
  SupplierBlacklistItemDetail,
  SupplierBlockItemDetail,
  SupplierAccountAuditParams,
  SrmSupplierFile,
  AddSupplementaryFileDTO,
  UpdateSupplementaryFileDTO,
  SrmSupplierMaterial,
  BindMaterialToSupplierDTO,
  SupplierMaterialListItem,
  SupplierPageVo,
  SupplierPageDto,
} from "@/types/apis";
import request from "@/utils/request";

/**
 * 供应商注册分页列表
 * @param data 请求参数
 */
export const registrationPage = (data: any) =>
  request.post("/purchase/supplierRegister/registrationPage", data);

/**
 * 获取邮件邀请模板
 */
export const getInviteEmailTemplate = () =>
  request.get("/purchase/supplierRegister/inviteEmailTemplate");

/**
 * 发送邮件邀请供应商
 * @param data 请求参数
 */
export const inviteSupplier = (data: {
  supplierName: string;
  email: string;
  emailSubject: string;
  emailBody: string;
  supplierId?: number;
}) => request.post("/purchase/supplierRegister/inviteSupplier", data);

/**
 * 获取准入评估模板树形结构
 */
export const getAdmissionEvaluationCategoryTree = () =>
  request.get("/purchase/evaluationCategory/tree");

/**
 * 保存准入评估模板树形结构
 * @param data 树形结构数据
 */
export const saveAdmissionEvaluationCategoryTree = (data: any) =>
  request.post("/evaluationCategory/saveTree", data);

/**
 * 保存准入评估模板分支
 * @param data 分支数据
 */
export const saveAdmissionEvaluationCategoryBranch = (data: any) =>
  request.post("/purchase/evaluationCategory/saveBranch", data);

/**
 * 获取准入评估模板分支详情
 * @param id 分支ID
 */
export const getAdmissionEvaluationCategoryBranch = (id: string) =>
  request.get("/purchase/evaluationCategory/branch", {
    params: { id },
  });

/**
 * 删除准入评估模板
 * @param id 模板ID
 */
export const deleteAdmissionEvaluationCategory = (id: number) =>
  request.delete("/purchase/evaluationCategory/delete", {
    params: { id },
  });

/**
 * 获取所有部门下拉列表数据
 */
export const getDepartmentList = () => request.post("/purchase/dept/list", {});

/**
 * 获取用户列表（用于搜索申请人）
 * @param data 请求参数，包含 nickName 模糊查询条件
 */
export const getUserList = (data: { nickName: string }) =>
  request.post("/purchase/user/page", data);

/**
 * 供应商绩效考核信息列表
 * @param data
 */
export const getPerformanceEvaluationList = (
  data: PerformanceEvaluationListParams,
) =>
  request.post<PageResponse<PerformanceEvaluationListItem>>(
    "/purchase/performance/evaluation/page",
    data,
  );

/**
 * 获取供应商简单信息列表
 * @param data
 */
export const getSupplierUserSimpleList = (data: SupplierUserSimpleListParams) =>
  request.post<SupplierUserSimpleListItem[]>(
    "/purchase/supplierUser/simpleList",
    data,
  );

/**
 * 获取访问审核风险等级供应商列表
 * @param data
 */
export const getSupplierListInVisitAuditRisk = (
  data: SupplierUserSimpleListParams,
) =>
  request.post<SupplierUserSimpleListItem[]>(
    "/purchase/supplierUser/listInVisitAuditRisk",
    data,
  );

// 新增供应商绩效考核信息
export const createSupplierPerformance = (
  data: SavePerformanceEvaluationSchema,
) => request.post<ApiResponse>("/purchase/performance/evaluation/save", data);

/**
 * 更新评估考核建议
 * @param data
 */
export const updatePerformanceEvaluationAdvice = (
  data: UpdatePerformanceEvaluationAdviceSchema,
) =>
  request.post<ApiResponse>(
    "/purchase/performance/evaluation/updateEvaluationAdvice",
    data,
  );

/**
 * 删除供应商绩效考核信息
 * @param id 供应商绩效考核记录id
 */
export const deleteSupplierPerformanceEvaluation = (id: number) =>
  request.post<ApiResponse>(
    `/purchase/performance/evaluation/delete/${id}`,
    {},
  );

/**
 * 获取供应商绩效考核明细信息
 * @param id
 * @returns
 */
export const getSupplierPerformanceEvalutionDetail = (id: number) =>
  request.post<ApiResponse<PerformanceEvaluationDetail>>(
    `/purchase/performance/evaluation/details/${id}`,
    {},
  );

/**
 * 采购端-全部阶段，查看供应商所有文件，包含基础资质、补充资质、物料资质
 */
export const getSrmSupplierFiles = (supplierId: string) =>
  request.get<
    ApiResponse<{
      baseFileList: SrmSupplierFile[];
      materialList: SrmSupplierMaterial[];
    }>
  >("/purchase/supplier/supplementaryFile/fileInfo", {
    params: {
      supplierId,
    },
  });

/** 供应商绑定物料 */
export const bindMaterialToSupplier = (data: BindMaterialToSupplierDTO) =>
  request.post("/purchase/supplier/supplementaryFile/materialBind", data);

/** 编辑物料信息*/
export const updateSupplierMaterial = (data: UpdateSupplierMaterialSchema) =>
  request.post("/purchase/supplier/supplementaryFile/materialUpdate", data);

/**
 * 删除物料信息
 * @param id 物料id
 */
export const deleteSupplierMaterial = (id: number) =>
  request.post(
    `/purchase/supplier/supplementaryFile/materialDelete`,
    {},
    {
      params: {
        supplierMaterialId: id,
      },
    },
  );

/**
 * 获取供应商绩效考核指标列表
 */
export const getPurchasePerformanceEvaluationIndicator = () =>
  request.post<ApiResponse<PurchasePerformanceEvaluationIndicator[]>>(
    "/purchase/performance/evaluation/indicator/list",
    {},
  );

/** 删除供应商绩效考核指标 */
export const deletePurchasePerformanceEvaluationIndicator = (id: number) =>
  request.post(`/purchase/performance/evaluation/indicator/delete/${id}`, {});

/** 添加补充资质(企业文件)  */
export const addSupplementaryFile = (data: AddSupplementaryFileDTO) =>
  request.post("/purchase/supplier/supplementaryFile/addExtraFile", data);

/**
 * 添加物料资质
 * @description 仅添加资质名字、备注等，文件由供应商上传
 */
export const addSupplementaryMaterialFile = (data: AddSupplementaryFileDTO) =>
  request.post("/purchase/supplier/supplementaryFile/addMaterialFile", data);

/** 根据物料code获取物料名称 */
export const getMaterialNameByCode = (materialCode: string) =>
  request.get<ApiResponse<string | null>>(
    `/purchase/supplier/supplementaryFile/materialByCode`,
    {
      params: {
        materialCode,
      },
    },
  );

/**
 * 发送通知提醒
 * @param data
 */
export const sendNotice = (fileId: number) =>
  request.post(
    "/purchase/supplier/supplementaryFile/sendNotification",
    {},
    { params: { fileId } },
  );

/**
 * 供应商全部阶段列表
 * @param data
 * @returns
 */
export const queryAllStageSupplierUser = (
  data: QuerySupplierUserAllStageSchema,
) =>
  request.post<PageResponse<AllStageSupplierUserListItem>>(
    "/purchase/supplierUser/allStage",
    data,
  );
/**
 * 获取物料状态列表
 */
export const getMaterialStatusList = () =>
  request.get("/purchase/evaluationCategory/materialStatusList");

/**
 * 获取评估流程列表
 * @param materialTypeId 物料类型ID
 * @param deptId 部门ID
 */
export const getEvaluationProcessList = (
  materialTypeId: string,
  deptId: string,
) =>
  request.get(
    `/purchase/evaluationCategory/processList?materialTypeId=${materialTypeId}&deptId=${deptId}`,
  );

/**
 * 获取评估分类列表（用于评估邀请中的下拉选）
 * @param id 物料状态ID
 */
export const getEvaluationCategoryList = (id: string) =>
  request.get(`/purchase/evaluationCategory/childList?id=${id}`);

/**
 * 提交准入评估申请
 * @param data 评估申请数据
 */
export const submitAssessmentRequest = (data: any) =>
  request.post("/purchase/access/evaluation/submission", data);

/**
 * 获取准入评估列表
 * @param data 请求参数
 */
export const getAccessEvaluationPage = (data: any) =>
  request.post("/purchase/access/evaluation/page", data);

/**
 * 获取准入申请列表
 * @param data 请求参数：pageNum, pageSize, supplierCode, materialCategoryValue, applicationStatus, applicant
 */
export const getAdmissionApplicationPage = (data: any) =>
  request.post("/purchase/admission/application/page", data);

/**
 * 获取准入申请详情
 * @param id 准入申请记录ID
 */
export const getAdmissionApplicationById = (id: string | number) =>
  request.get<ApiResponse>(`/purchase/admission/application/getById/${id}`);

/**
 * 获取准入评估详情
 * @param id 评估记录ID
 */
export const getAccessEvaluationById = (id: string | number) =>
  request.get(`/purchase/access/evaluation/getInfoById/${id}`);

/**
 * 获取准入申请的评估详情
 * @param id 准入申请记录ID
 */
export const getAccessEvaluationInfoById = (id: string | number) =>
  request.get(
    `/purchase/admission/application/getAccessEvaluationInfoById/${id}`,
  );

/**
 * 获取准入评估详情（编辑模式）
 * @param id 评估记录ID
 */
export const getAccessEvaluationByEditId = (id: string | number) =>
  request.get(`/purchase/access/evaluation/editInfoById/${id}`);

/**
 * 生成流程单号
 * @param flowCodeEnums 流程编码枚举值
 */
export const generateProcessCode = (flowCodeEnums: string) =>
  request.get("/view/generate/process/code", { params: { flowCodeEnums } });

/**
 * 发送评估邀请
 * @param data 邀请数据
 */
export const sendEvaluationInvitation = (data: any) =>
  request.post("/purchase/access/evaluation/sendInvitation", data);

/**
 * 删除评估邀请
 * @param id 邀请ID
 */
export const deleteEvaluationInvitation = (id: string | number) =>
  request.delete<ApiResponse>(
    `/purchase/access/evaluation/deleteInvitation/${id}`,
  );

/**
 * 获取评估邀请信息
 * @param evaluationId 评估ID
 * @param phaseIds 阶段ID（逗号分隔）
 */
export const getEvaluationInvitation = (
  evaluationId: string | number,
  phaseIds: string,
) =>
  request.get("/purchase/access/evaluation/getEvaluationInvitation", {
    params: {
      evaluationId,
      phaseIds,
    },
  });

/**
 * 审核供应商拓展文件
 * @param data
 * @returns
 */
export const auditSupplierFile = (data: AuditSupplierFileSchema) =>
  request.put("/purchase/supplier/supplementaryFile/fileAudit", data);

/**
 * @deprecated
 * 新增供应商拓展文件（直接审核通过）
 * @param data
 * @returns
 */
export const saveSupplierFile = (data: SaveSupplementaryFileSchema) =>
  request.post("/purchase/supplier/supplementaryFile/saveFile", data);

/** 采购人员添加补充资质并上传 */
export const addExtraFileByPurchase = (data: AddSupplementaryFileDTO) =>
  request.post(
    "/purchase/supplier/supplementaryFile/addExtraFileByPurchase",
    data,
  );

/** 采购人员添加物料资质并上传 */
export const addMaterialFileByPurchase = (data: AddSupplementaryFileDTO) =>
  request.post(
    "/purchase/supplier/supplementaryFile/addMaterialFileByPurchase",
    data,
  );

/** 采购人员更新、上传资质 */
export const updateSupplementaryFile = (data: UpdateSupplementaryFileDTO) =>
  request.post("/purchase/supplier/supplementaryFile/updateFile", data);

/**
 * 获取供应商的账号列表
 * @param supplierId
 * @returns
 */
export const getSupplierUserAccountList = (supplierId: string) =>
  request.get<ApiResponse<SupplierUserAccountListItem[]>>(
    "/purchase/supplierUser/accountList",
    {
      params: {
        supplierId,
      },
    },
  );

/** 禁用供应商子账号 */
export const disableSupplierSubAccount = (registrationId: number) =>
  request.put(
    "/purchase/supplierUser/accountDisable",
    {},
    {
      params: {
        registrationId,
      },
    },
  );

/** 启用供应商子账号 */
export const enableSupplierSubAccount = (registrationId: number) =>
  request.put(
    "/purchase/supplierUser/accountEnable",
    {},
    {
      params: {
        registrationId,
      },
    },
  );

/** 供应商帐号审核 */
export const auditSupplierAccount = (data: SupplierAccountAuditParams) =>
  request.put("/purchase/supplierUser/accountAudit", data);

/**
 * 获取供应商的关联供应商列表
 * @param supplierId
 * @returns
 */
export const getSupplierRelationList = (supplierId: string) =>
  request.get<ApiResponse<SupplierRelationListItem[]>>(
    "/purchase/supplierUser/relationList",
    {
      params: {
        supplierId,
      },
    },
  );

/**
 * 保存供应商绩效考核指标
 * @param data
 * @returns
 */
export const saveSupplierPerformanceEvaluationIndicator = (
  data: SaveOrUpdateSupplierPerformanceEvaluationIndicatorSchema,
) => request.post("/purchase/performance/evaluation/indicator/save", data);

/**
 * 更新供应商绩效考核指标
 * @param data
 * @returns
 */
export const updateSupplierPerformanceEvaluationIndicator = (
  data: SaveOrUpdateSupplierPerformanceEvaluationIndicatorSchema,
) => request.post("/purchase/performance/evaluation/indicator/update", data);

/**
 * 获取供应商类别对应绩效考核指标列表
 * @param supplierCategoryId
 * @returns
 */
export const getSupplierPerformanceEvaluationIndicatorDetail = (
  supplierCategoryId: string,
) =>
  request.post<ApiResponse<PurchasePerformanceEvaluationIndicator>>(
    `/purchase/performance/evaluation/indicator/category/${supplierCategoryId}`,
    {},
  );

/**
 * 分页查询当前用户的通知公告列表
 * @param data
 * @returns
 */
export const getNoticePageList = (data: QueryNoticePageList) =>
  request.post<PageResponse<NoticeInfoListItem>>(
    "/purchase/notice/page/current",
    data,
  );

/**
 * 物料冻结分页查询
 * @param data
 * @returns
 */
export const getSupplierBlockPageList = (data: any) =>
  request.post<PageResponse<SupplierBlockListItem>>(
    "/purchase/supplier/material/block/page",
    data,
  );

/**
 * 供应商物料冻结信息-根据id查询详情
 * @returns
 */
export const getSupplierBlockDetail = (id: number) =>
  request.get<ApiResponse<SupplierBlockItemDetail>>(
    `/purchase/supplier/material/block/getInfoById/${id}`,
  );

/**
 * 冻结记录根据id删除
 */
export const deleteBlockRecord = (id: number) =>
  request.delete<ApiResponse>(
    `/purchase/supplier/material/block/removeById/${id}`,
  );

/**
 * 供应商黑名单分页查询
 * @param data
 * @returns
 */
export const getSupplierBlacklistPageList = (data: any) =>
  request.post<PageResponse<SupplierBlacklistItem>>(
    "/purchase/supplier/material/blacklist/page",
    data,
  );

/**
 * 供应商黑名单信息-根据id查询详情
 * @returns
 */
export const getSupplierBlacklistDetail = (id: number) =>
  request.get<ApiResponse<SupplierBlacklistItemDetail>>(
    `/purchase/supplier/material/blacklist/getInfoById/${id}`,
  );

/**
 * 供应商黑名单根据id删除
 */
export const deleteblacklistRecord = (id: number) =>
  request.delete<ApiResponse>(
    `/purchase/supplier/material/blacklist/removeById/${id}`,
  );

/**
 * 获取流程历史记录
 * @param insId 流程实例ID
 */
export const getHistoryByInsId = (insId: string | number) =>
  request.get<ApiResponse>(`/view/getHisByIns/${insId}`);

/**
 * 发起会议审批
 * @param data 审批参数
 */
export const startMeetingApproval = (data: any) =>
  request.post<ApiResponse>("/view/start", data);

/**
 * 审批通过/驳回接口
 * @param data 审批参数
 * @param data.skipType 决策类型：PASS-通过，不传-驳回
 * @param data.message 意见说明
 * @param data.taskId 任务ID（编辑接口返回的taskId）
 * @param data.flowCode 流程编码：ZHUN_RU_PING_GU
 * @param data.variable 流程变量，包含toGm字段表示是否需要GM决策
 */
export const approvalPass = (data: any, taskId: string) =>
  request.post<ApiResponse>("/view/pass?taskId=" + taskId, data);

/**
 * 获取供应商详情
 * @param supplierId 供应商ID
 * @param audit 审核标记（true为审核模式，false为编辑模式）
 * @returns 供应商详情信息
 */
export const getSupplierDetail = (
  supplierId: string | number,
  audit: boolean = false,
) =>
  request.get<ApiResponse>("/purchase/supplierRegister/supplierDetail", {
    params: {
      supplierId,
      audit,
    },
  });

/**
 * 根据业务ID获取供应商详情（用于工作台代办）
 * @param businessId 业务ID
 * @returns 供应商详情信息
 */
export const getSupplierDetailByBusinessId = (businessId: string | number) =>
  request.get<ApiResponse>(
    "/purchase/supplierRegister/supplierDetailByBusinessId",
    {
      params: {
        businessId,
      },
    },
  );

/**
 * 价格申请分页查询
 * @param data 请求参数：pageNum, pageSize, supplierCode, supplierName, materialCode, materialName, applicant, applicationStatus, factoryValue
 */
export const getPriceApplicationPage = (data: any) =>
  request.post("/purchase/price/application/page", data);

/**
 * 获取供应商用户信息
 * @param supplierCode 供应商编号
 * @param currentStage 当前阶段，默认值3
 */
export const getSupplierUserInfo = (supplierCode: string) =>
  request.get<ApiResponse>("/purchase/supplierUser/getSupplierUserInfo", {
    params: {
      supplierCode,
    },
  });

/**
 * 检查是否首次采购
 * @param params 请求参数
 * @param params.fdFactory 公司值
 * @param params.fdMaterialCode 物料编号
 * @param params.fdSupplierCode 供应商编号
 */
export const checkFirstPurchase = (params: {
  fdFactory: string;
  fdMaterialCode: string;
  fdSupplierCode: string;
}) =>
  request.get<ApiResponse>("/sap/isFirstPurchase", {
    params,
  });

/**
 * 获取汇率信息
 * @param inputCurrency 输入货币代码 (如: USD, EUR, CNY 等)
 * @returns 汇率数据
 */
export const getExchangeRate = (inputCurrency: string) =>
  request.get<ApiResponse>("/sap/getExchangeRate", {
    params: {
      inputCurrency,
    },
  });

/**
 * 获取价格申请详情
 * @param id 价格申请记录ID
 */
export const getPriceApplicationById = (id: string | number) =>
  request.get<ApiResponse>(`/purchase/price/application/getById/${id}`);

/**
 * 删除价格申请
 * @param id 价格申请记录ID
 */
export const deletePriceApplication = (id: string | number) =>
  request.delete<ApiResponse>(`/purchase/price/application/removeById/${id}`);

/**
 * SAP 检测开户行行号是否存在
 * @param openBankNumbers 开户行行号数组
 */
export const checkBatchOpenBankNumber = (data: { openBankNumbers: string[] }) =>
  request.post<ApiResponse>("/sap/checkBatchOpenBankNumber", data);

/**
 * 供应商风险等级分页列表
 * @param data 请求参数
 */
export const getSupplierRiskLevelPage = (data: any) =>
  request.post("/purchase/supplier/risk/register/page", data);

/**
 * 根据供应商编号获取供应商详情信息
 * @param supplierCode 供应商编号
 */
export const getSupplierDetailByCode = (supplierCode: string) =>
  request.get<ApiResponse>("/purchase/supplierUser/getDetailInfoByCode", {
    params: {
      supplierCode,
    },
  });

/**
 * 根据ID获取供应商风险登记详情信息（用于编辑）
 * @param id 风险登记记录ID
 */
export const getRiskRegisterInfoById = (id: number | string) =>
  request.get<ApiResponse>(
    `/purchase/supplier/risk/register/getInfoById/${id}`,
  );

/**
 * 物料异常登记分页列表
 * @param data 请求参数
 */
export const getMaterialExceptionPage = (data: any) =>
  request.post("/purchase/supplier/material/abnormal/register/page", data);

/**
 * 根据物料编号获取物料信息
 * @param materialCode 物料编号
 */
export const getMaterialInfoByCode = (materialCode: string) =>
  request.get<ApiResponse>(
    `/purchase/supplier/material/getMaterialInfoByCode/${materialCode}`,
  );

/**
 * 根据公司、物料编号、批号获取供应商信息
 * @param fdFactory 公司编号（工厂值）
 * @param materialCode 物料编号
 * @param batchNumber 批号
 */
export const getSupplierInfo = (
  fdFactory: string,
  materialCode: string,
  batchNumber: string,
) =>
  request.get<ApiResponse>("/sap/getSupplierInfo", {
    params: {
      fdFactory,
      materialCode,
      batchNumber,
    },
  });

/**
 * 根据ID获取物料异常登记详情信息（用于编辑）
 * @param id 物料异常登记记录ID
 */
export const getMaterialExceptionInfoById = (id: number | string) =>
  request.get<ApiResponse>(
    `/purchase/supplier/material/abnormal/register/getInfoById/${id}`,
  );

/**
 * 获取供应商物料列表
 * @returns 供应商物料列表
 */
export const getSupplierMaterials = (data: {
  supplierUserId?: string | number;
  materialCode?: string;
  company?: string;
}) =>
  request.get<ApiResponse<SupplierMaterialListItem[]>>(
    `/purchase/supplier/material/listBySupplierUserId`,
    { params: data },
  );

/**
 * 上传签名文件
 * @param data 上传参数
 * @param data.opinion 审批意见（approved-建议准入，rejected-不建议准入）
 * @param data.remark 详细意见
 * @param data.approverNo 审批人工号
 * @param data.approverName 审批人名称
 * @param data.fileBase64Data 签名图片(base64)
 * @param data.code 供应商编号
 * @param data.processCode 流程编号
 * @param data.token 企业微信登录返回的 token
 */
export const uploadSignatureFile = (data: {
  opinion: string;
  remark: string;
  approverNo: string;
  approverName: string;
  fileBase64Data: string;
  code: string;
  processCode: string;
  token: string;
}) => {
  const { token, ...requestData } = data;
  return request.post<ApiResponse>("/oss/upload/file", requestData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * 获取拜访记录分页列表
 * @param data 请求参数：pageNum, pageSize
 */
export const getVisitRecordPage = (data: any) =>
  request.post("/purchase/visitRecord/page", data);

/**
 * 根据供应商编号获取供应商列表（用于拜访记录模糊查询）
 * @param supplierCode 供应商编号
 */
export const getVisitRecordSupplierList = (supplierCode: string) =>
  request.get<ApiResponse>("/purchase/visitRecord/supplierList", {
    params: {
      supplierCode,
    },
  });

/**
 * 根据供应商ID获取供应商详细信息（用于拜访记录）
 * @param supplierId 供应商ID
 */
export const getVisitRecordSupplierInfo = (supplierId: string | number) =>
  request.get<ApiResponse>("/purchase/visitRecord/supplierInfo", {
    params: {
      supplierId,
    },
  });

/**
 * 新增拜访记录
 * @param data 拜访记录数据
 * @param data.supplierId 供应商ID
 * @param data.visitCycle 拜访频次（次/年）
 * @param data.visitType 拜访类型
 * @param data.visitStartDate 拜访开始日期
 * @param data.visitEndDate 拜访结束日期
 * @param data.visitReason 拜访原因
 * @param data.visitContent 拜访内容
 * @param data.visitPrepareDoc 拜访准备相关资料（多个字符串逗号隔开）
 * @param data.notifySupplier 是否通知供应商
 */
export const addVisitRecord = (data: {
  supplierId: string | number;
  visitCycle: string | number;
  visitType: string | number;
  visitStartDate: string;
  visitEndDate: string;
  visitReason: string;
  visitContent: string;
  visitPrepareDoc: string;
  notifySupplier: boolean;
}) => request.post<ApiResponse>("/purchase/visitRecord/add", data);

/**
 * 根据ID获取拜访记录详情信息（用于编辑）
 * @param id 拜访记录ID
 */
export const getVisitRecordById = (id: string | number) =>
  request.get<ApiResponse>("/purchase/visitRecord/getInfoById", {
    params: {
      id,
    },
  });

/**
 * 根据visitRecordId获取拜访记录详情信息（用于查看）
 * @param visitRecordId 拜访记录ID
 */
export const getVisitRecordInfo = (visitRecordId: string | number) =>
  request.get<ApiResponse>("/purchase/visitRecord/info", {
    params: {
      visitRecordId,
    },
  });

/**
 * 修改拜访记录
 * @param data 拜访记录数据
 * @param data.visitRecordId 拜访记录ID
 * @param data.supplierId 供应商ID
 * @param data.visitCycle 拜访频次（次/年）
 * @param data.visitType 拜访类型
 * @param data.visitStartDate 拜访开始日期
 * @param data.visitEndDate 拜访结束日期
 * @param data.visitReason 拜访原因
 * @param data.visitContent 拜访内容
 * @param data.visitPrepareDoc 拜访准备相关资料（多个字符串逗号隔开）
 * @param data.notifySupplier 是否通知供应商
 */
export const modifyVisitRecord = (data: {
  visitRecordId: string | number;
  supplierId: string | number;
  visitCycle: string | number;
  visitType: string | number;
  visitStartDate: string;
  visitEndDate: string;
  visitReason: string;
  visitContent: string;
  visitPrepareDoc: string;
  notifySupplier: boolean;
}) => request.put<ApiResponse>("/purchase/visitRecord/modify", data);

/**
 * 通知供应商拜访
 * @param visitRecordId 拜访记录ID
 */
export const notifySupplierVisit = (visitRecordId: string | number) =>
  request.put<ApiResponse>(
    "/purchase/visitRecord/notifySupplier",
    {},
    {
      params: {
        visitRecordId,
      },
    },
  );

/**
 * 填写拜访结果
 * @param data 拜访结果数据
 * @param data.visitRecordId 拜访记录ID
 * @param data.supplierId 供应商ID
 * @param data.visitCycle 拜访频次（次/年）
 * @param data.visitType 拜访类型
 * @param data.visitStartDate 拜访开始日期
 * @param data.visitEndDate 拜访结束日期
 * @param data.visitReason 拜访原因
 * @param data.visitContent 拜访内容
 * @param data.visitPrepareDoc 拜访准备相关资料（多个字符串逗号隔开）
 * @param data.visitMeetingMinutes 拜访会议纪要
 * @param data.visitMeetingDoc 会议相关资料（多个字符串逗号隔开）
 */
export const fillVisitRecord = (data: {
  visitRecordId: string | number;
  supplierId: string | number;
  visitCycle: string | number;
  visitType: string | number;
  visitStartDate: string;
  visitEndDate: string;
  visitReason: string;
  visitContent: string;
  visitPrepareDoc: string;
  visitMeetingMinutes: string;
  visitMeetingDoc: string;
}) => request.put<ApiResponse>("/purchase/visitRecord/fillInResults", data);

/**
 * 根据供应商名称模糊查询供应商列表（用于企查查）
 * @param supplierName 供应商名称
 */
export const getSupplierListByQcc = (
  supplierName: string,
  pageIndex: number = 1,
) =>
  request.get<ApiResponse>("/purchase/supplierUser/listByQcc", {
    params: {
      supplierName,
      pageIndex,
    },
  });

/**
 * 根据统一社会信用代码获取供应商详情（企查查）
 * @param supplierName 统一社会信用代码（socialCreditCode）
 */
export const getSupplierDetailByQcc = (supplierName: string) =>
  request.get<ApiResponse>("/purchase/supplierUser/detailByQcc", {
    params: {
      supplierName,
    },
  });

/**
 * 代理注册供应商
 * @param data 代理注册数据
 */
export const proxyRegistration = (data: any) =>
  request.post<ApiResponse>(
    "/purchase/supplierRegister/proxyRegistration",
    data,
  );

/** 分页获取供应商信息接口 */
export const getSupplierPage = (data: SupplierPageDto) =>
  request.post<PageResponse<SupplierPageVo>>(
    "/purchase/supplierUser/pageList",
    data,
  );

/**
 * 编辑供应商信息
 * @param data 编辑数据（包含 supplierId 字段）
 * @param data.supplierId 供应商ID（从 supplierDetail 接口的 id 字段获取）
 */
export const updateSupplierInfo = (data: any) =>
  request.post<ApiResponse>("/purchase/supplierRegister/updateInfo", data);

/**
 * 审核供应商信息
 * @param data 审核数据
 * @param data.supplierId 供应商ID（从 supplierDetail 接口的 id 字段获取）
 * @param data.auditStatus 审核状态
 * @param data.auditOpinion 审核意见
 */
export const auditSupplierInfo = (data: {
  supplierId: string | number;
  auditStatus: string | number;
  auditOpinion: string;
  [key: string]: any;
}) => request.post<ApiResponse>("/purchase/supplierRegister/updateInfo", data);

/**
 * 获取审计记录分页列表
 * @param data 请求参数：pageNum, pageSize
 */
export const getAuditRecordPage = (data: any) =>
  request.post("/purchase/auditRecord/page", data);

/**
 * 根据供应商编号获取供应商列表（用于审核记录模糊查询）
 * @param supplierCode 供应商编号
 */
export const getAuditRecordSupplierList = (supplierCode: string) =>
  request.get<ApiResponse>("/purchase/auditRecord/supplierList", {
    params: {
      supplierCode,
    },
  });

/**
 * 根据供应商ID获取供应商详细信息（用于审核记录）
 * @param supplierId 供应商ID
 */
export const getAuditRecordSupplierInfo = (supplierId: string | number) =>
  request.get<ApiResponse>("/purchase/auditRecord/supplierInfo", {
    params: {
      supplierId,
    },
  });

/**
 * 新增审核记录
 * @param data 审核记录数据
 * @param data.supplierId 供应商ID
 * @param data.auditCompany 审计需求工厂
 * @param data.auditMethod 审计方式
 * @param data.auditStartDate 审计开始日期
 * @param data.auditEndDate 审计结束日期
 * @param data.auditReason 审计原因
 * @param data.auditContent 审计内容
 */
export const addAuditRecord = (data: {
  supplierId: string | number;
  auditCompany: string;
  auditMethod?: number;
  auditStartDate: string;
  auditEndDate: string;
  auditReason: string;
  auditContent: string;
}) => request.post<ApiResponse>("/purchase/auditRecord/add", data);

/**
 * 根据auditRecordId获取审计记录详情信息（用于查看）
 * @param auditRecordId 审计记录ID
 */
export const getAuditRecordInfo = (auditRecordId: string | number) =>
  request.get<ApiResponse>("/purchase/auditRecord/info", {
    params: {
      auditRecordId,
    },
  });

/**
 * 修改审计记录
 * @param data 审计记录数据
 * @param data.auditRecordId 审计记录ID
 * @param data.supplierId 供应商ID
 * @param data.auditCompany 审计需求工厂
 * @param data.auditMethod 审计方式
 * @param data.auditStartDate 审计开始日期
 * @param data.auditEndDate 审计结束日期
 * @param data.auditReason 审计原因
 * @param data.auditContent 审计内容
 */
export const modifyAuditRecord = (data: {
  auditRecordId: string | number;
  supplierId: string | number;
  auditCompany: string;
  auditMethod?: number;
  auditStartDate: string;
  auditEndDate: string;
  auditReason: string;
  auditContent: string;
}) => request.put<ApiResponse>("/purchase/auditRecord/modify", data);

/**
 * 填写审计结果
 * @param data 审计记录数据
 * @param data.auditRecordId 审计记录ID
 * @param data.supplierId 供应商ID
 * @param data.auditCompany 审计需求工厂
 * @param data.auditMethod 审计方式
 * @param data.auditStartDate 审计开始日期
 * @param data.auditEndDate 审计结束日期
 * @param data.auditReason 审计原因
 * @param data.auditContent 审计内容
 * @param data.auditLevel 审计等级
 * @param data.auditResultEvaluation 审计结果评价
 * @param data.auditResultDoc 审计结果附件
 */
export const fillAuditRecord = (data: {
  auditRecordId: string | number;
  supplierId: string | number;
  auditCompany: string;
  auditMethod?: number;
  auditStartDate: string;
  auditEndDate: string;
  auditReason: string;
  auditContent: string;
  auditLevel: string;
  auditResultEvaluation: string;
  auditResultDoc: string;
}) => request.put<ApiResponse>("/purchase/auditRecord/fillInResults", data);

/**
 * 通知供应商审计
 * @param auditRecordId 审计记录ID
 */
export const notifySupplierAudit = (auditRecordId: string | number) =>
  request.put<ApiResponse>(
    "/purchase/auditRecord/notifySupplier",
    {},
    {
      params: {
        auditRecordId,
      },
    },
  );

/**
 * 删除准入申请
 * @param id 准入申请记录ID
 */
export const deleteAdmissionApplication = (id: string | number) =>
  request.delete<ApiResponse>(`/purchase/admission/application/remove/${id}`);

/**
 * 删除供应商注册记录
 * @param supplierId 供应商ID
 */
export const deleteSupplierRegister = (supplierId: string | number) =>
  request.delete<ApiResponse>("/purchase/supplierRegister/delete", {
    params: {
      supplierId,
    },
  });
